import { APIClient } from "../helpers/api_helper";
import { ApiResponse } from "../types/api";

const api = new APIClient();

export type ChecklistPhase = "hygiene" | "application" | "aftercare" | "other";
export type ChecklistStepStatus = "pending" | "done" | "skipped";

export interface ClinicalChecklistTemplateStep {
  id?: number;
  clinical_checklist_template_id?: number;
  sort_order: number;
  title: string;
  description?: string | null;
  phase?: ChecklistPhase | null;
}

export interface ClinicalChecklistTemplate {
  id: number;
  tenant_id: number;
  code: string;
  name: string;
  description: string | null;
  sort_order: number;
  is_active: boolean;
  is_system: boolean;
  is_default: boolean;
  steps_count?: number;
  steps?: ClinicalChecklistTemplateStep[];
}

export interface ClinicalProtocolChecklistStep {
  id: number;
  clinical_protocol_session_id: number;
  clinical_checklist_template_step_id: number | null;
  sort_order: number;
  title: string;
  description: string | null;
  phase: ChecklistPhase | null;
  status: ChecklistStepStatus;
  done_at: string | null;
  done_by_user_id: number | null;
  done_by?: { id: number; name: string } | null;
}

export interface ClinicalProtocolChecklistProgress {
  done: number;
  skipped: number;
  pending: number;
  total: number;
  label: string;
}

export interface ClinicalProtocolChecklistPayload {
  steps: ClinicalProtocolChecklistStep[];
  progress: ClinicalProtocolChecklistProgress;
}

export interface CreateClinicalChecklistTemplatePayload {
  name: string;
  code?: string | null;
  description?: string | null;
  sort_order?: number;
  is_active?: boolean;
  is_default?: boolean;
  steps?: Array<{
    title: string;
    description?: string | null;
    phase?: ChecklistPhase | null;
    sort_order?: number;
  }>;
}

export interface UpdateClinicalChecklistTemplatePayload {
  name?: string;
  description?: string | null;
  sort_order?: number;
  is_active?: boolean;
  is_default?: boolean;
  steps?: Array<{
    title: string;
    description?: string | null;
    phase?: ChecklistPhase | null;
    sort_order?: number;
  }>;
}

export const listClinicalChecklistTemplates = (params: {
  include_inactive?: boolean;
  is_active?: boolean;
} = {}) =>
  api.get<ApiResponse<ClinicalChecklistTemplate[]>>(
    "/clinical-checklist-templates",
    params
  );

export const createClinicalChecklistTemplate = (
  payload: CreateClinicalChecklistTemplatePayload
) =>
  api.create<ApiResponse<ClinicalChecklistTemplate>>(
    "/clinical-checklist-templates",
    payload
  );

export const updateClinicalChecklistTemplate = (
  id: number,
  payload: UpdateClinicalChecklistTemplatePayload
) =>
  api.update<ApiResponse<ClinicalChecklistTemplate>>(
    `/clinical-checklist-templates/${id}`,
    payload
  );

export const deactivateClinicalChecklistTemplate = (id: number) =>
  api.delete<ApiResponse<ClinicalChecklistTemplate>>(
    `/clinical-checklist-templates/${id}`
  );

export const getSessionChecklist = (protocolId: number, sessionId: number) =>
  api.get<ApiResponse<ClinicalProtocolChecklistPayload>>(
    `/clinical-protocols/${protocolId}/sessions/${sessionId}/checklist`
  );

export const updateSessionChecklistStep = (
  protocolId: number,
  sessionId: number,
  stepId: number,
  payload: { status: ChecklistStepStatus }
) =>
  api.update<
    ApiResponse<{
      step: ClinicalProtocolChecklistStep;
      steps: ClinicalProtocolChecklistStep[];
      progress: ClinicalProtocolChecklistProgress;
    }>
  >(
    `/clinical-protocols/${protocolId}/sessions/${sessionId}/checklist/steps/${stepId}`,
    payload
  );

export const markAllSessionChecklist = (
  protocolId: number,
  sessionId: number,
  payload: { status?: ChecklistStepStatus } = {}
) =>
  api.create<ApiResponse<ClinicalProtocolChecklistPayload>>(
    `/clinical-protocols/${protocolId}/sessions/${sessionId}/checklist/mark-all`,
    payload
  );
